import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { FiMenu, FiVideo } from 'react-icons/fi'
import apiInstance from '../../../../../Utils/api'
import { Delete } from '../../web_api/api'
import BranchButton from '../../branch/shared/BranchButton'
import BranchSearchInput from '../../branch/shared/BranchSearchInput'
import BranchSelect from '../../branch/shared/BranchSelect'
import BranchImageThumb from '../../branch/shared/BranchImageThumb'
import BranchActionIcons from '../../branch/shared/BranchActionIcons'
import BranchPagination from '../../branch/shared/BranchPagination'
import BranchEmptyState from '../../branch/shared/BranchEmptyState'
import BranchBreadcrumb from '../../branch/shared/BranchBreadcrumb'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import '../../branch/branch.css'
import '../testimonial.css'

export default function TestimonialList() {
  const [list, setList] = useState([])
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [videoFilter, setVideoFilter] = useState('')
  const [sortMode, setSortMode] = useState(false)
  const [sortItems, setSortItems] = useState([])
  const [draggedIdx, setDraggedIdx] = useState(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  const abortRef = useRef(null)
  const fetchIdRef = useRef(0)
  const perPage = 10

  const fetchList = async (p, kw) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    const id = ++fetchIdRef.current

    setLoading(true)
    try {
      const params = { keyword: kw || '', page: p, limit: perPage }
      if (activeFilter === 'active') params.isActive = true
      if (activeFilter === 'inactive') params.isActive = false
      if (categoryFilter) params.category = categoryFilter
      if (videoFilter) params.video = videoFilter
      const res = await apiInstance.get('testimonials', { params, signal: abortRef.current.signal })
      if (id !== fetchIdRef.current) return
      const data = res.data
      setList(data.list || [])
      setTotalCount(data._metadata?.total_count || 0)
      setPageCount(data._metadata?.page_count || 0)
    } catch (err) {
      if (err?.name === 'CanceledError') return
      console.error(err)
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchList(1, keyword)
      setPage(1)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [keyword])

  useEffect(() => {
    fetchList(1, keyword)
    setPage(1)
    setSortMode(false)
  }, [activeFilter, categoryFilter, videoFilter])

  useEffect(() => {
    fetchList(page, keyword)
  }, [page])

  const enterSortMode = async () => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    const id = ++fetchIdRef.current

    setSortMode(true)
    setLoading(true)
    try {
      const params = { keyword: keyword || '', page: 1, limit: 100, isActive: true }
      const res = await apiInstance.get('testimonials', { params, signal: abortRef.current.signal })
      if (id !== fetchIdRef.current) return
      const data = res.data
      setList(data.list || [])
      setSortItems(data.list || [])
    } catch (err) {
      if (err?.name === 'CanceledError') return
      console.error(err)
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }

  const cancelSort = () => {
    setSortMode(false)
    setSortItems([])
    setDraggedIdx(null)
    fetchList(1, keyword)
    setPage(1)
  }

  const saveSort = async () => {
    setSaving(true)
    try {
      const items = sortItems.map((s, i) => ({ _id: s._id, displayOrder: i + 1 }))
      await apiInstance.put('testimonials/sort', { items })
      Swal.fire({ title: 'Sort order updated!', icon: 'success', showConfirmButton: false, timer: 1500 })
      setSortMode(false)
      fetchList(1, keyword)
      setPage(1)
    } catch (err) {
      Swal.fire({ title: 'Failed to save sort order', icon: 'error', showConfirmButton: false, timer: 2000 })
    } finally {
      setSaving(false)
    }
  }

  const handleDragStart = (idx) => {
    setDraggedIdx(idx)
  }

  const handleDragOver = (e, idx) => {
    e.preventDefault()
    if (draggedIdx === null || draggedIdx === idx) return
    const updated = [...sortItems]
    const [moved] = updated.splice(draggedIdx, 1)
    updated.splice(idx, 0, moved)
    setSortItems(updated)
    setDraggedIdx(idx)
  }

  const handleDragEnd = () => {
    setDraggedIdx(null)
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This testimonial will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db2777',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    })
    if (!result.isConfirmed) return
    await Delete('/testimonial/', id)
    fetchList(page, keyword)
  }

  const handleActivate = async (id) => {
    try {
      await apiInstance.post('testimonial/' + id)
      fetchList(page, keyword)
    } catch (err) {
      console.error(err)
    }
  }

  const handleToggle = async (item) => {
    const action = item.isActive ? 'deactivate' : 'activate'
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `This testimonial will be ${action}d.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db2777',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Yes, ${action}`,
      cancelButtonText: 'Cancel'
    })
    if (!result.isConfirmed) return
    try {
      await apiInstance.post(`testimonial/${item._id}/${action}`)
      fetchList(page, keyword)
    } catch (err) {
      console.error(err)
    }
  }

  const items = sortMode ? sortItems : list
  const showSortBtn = activeFilter === 'active' && !sortMode

  return (
    <div className='branch-page'>
      <BranchBreadcrumb items={[
        { label: 'Testimonials', to: '/testimonial' },
        { label: 'Testimonial List' }
      ]} />

      <div className='branch-card'>
        <div className='branch-card-header'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className='branch-filter-group'>
              <button className={'branch-filter-btn' + (activeFilter === '' ? ' active' : '')} onClick={() => setActiveFilter('')}>All</button>
              <button className={'branch-filter-btn' + (activeFilter === 'active' ? ' active' : '')} onClick={() => setActiveFilter('active')}>Active</button>
              <button className={'branch-filter-btn' + (activeFilter === 'inactive' ? ' active' : '')} onClick={() => setActiveFilter('inactive')}>Inactive</button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {sortMode ? (
              <>
                <BranchButton onClick={cancelSort} variant='outline'>Cancel</BranchButton>
                <BranchButton onClick={saveSort} loading={saving}>Save Order</BranchButton>
              </>
            ) : (
              <>
                <BranchSelect
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  options={[
                    { value: '', label: 'All Categories' },
                    { value: 'customer', label: 'Customer' },
                    { value: 'celebrity', label: 'Celebrity' }
                  ]}
                  width={140}
                />
                <BranchSelect
                  value={videoFilter}
                  onChange={(e) => setVideoFilter(e.target.value)}
                  options={[
                    { value: '', label: 'All Videos' },
                    { value: 'true', label: 'Has Video' },
                    { value: 'false', label: 'No Video' }
                  ]}
                  width={130}
                />
                <BranchSearchInput
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (debounceRef.current) clearTimeout(debounceRef.current)
                      fetchList(1, keyword)
                      setPage(1)
                    }
                  }}
                  placeholder='Search testimonials...'
                />
                <BranchButton to='/testimonial-create'>+ Add</BranchButton>
                {showSortBtn && <BranchButton onClick={enterSortMode} variant='outline'>Reorder</BranchButton>}
              </>
            )}
          </div>
        </div>

        <div className='branch-card-body' style={{ padding: 0 }}>
          {loading ? (
            <div className='branch-loader'>
              <div className='branch-loader-spinner' />
              <div className='branch-loader-text'>Loading testimonials...</div>
            </div>
          ) : items.length === 0 ? (
            <BranchEmptyState title='No testimonials found' actionTo='/testimonial-create' />
          ) : (
            <>
              <div className='branch-table-wrapper'>
                <table className='branch-table'>
                  <thead>
                    <tr>
                      {sortMode && <th style={{ width: '4%' }} />}
                      <th style={{ width: sortMode ? '4%' : '5%' }}>#</th>
                      <th style={{ width: '8%' }}>Photo</th>
                      <th style={{ width: '18%' }}>Client</th>
                      <th style={{ width: '10%' }}>Category</th>
                      <th style={{ width: '8%' }}>Video</th>
                      <th style={{ width: '26%' }}>Review</th>
                      <th style={{ width: '8%' }}>Status</th>
                      <th style={{ width: '12%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr
                        key={item._id}
                        draggable={sortMode}
                        onDragStart={() => sortMode && handleDragStart(i)}
                        onDragOver={(e) => sortMode && handleDragOver(e, i)}
                        onDragEnd={handleDragEnd}
                        className={draggedIdx === i ? 'branch-dragging' : ''}
                        style={{ opacity: draggedIdx === i ? 0.5 : 1 }}
                      >
                        {sortMode && (
                          <td>
                            <span style={{ cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--branch-text-muted)' }}>
                              <FiMenu size={16} />
                            </span>
                          </td>
                        )}
                        <td>{(page - 1) * perPage + i + 1}</td>
                        <td>
                          <PhotoProvider>
                            <PhotoView src={item.clientImageUrl}>
                              <BranchImageThumb src={item.clientImageUrl} alt={item.clientName} />
                            </PhotoView>
                          </PhotoProvider>
                        </td>
                        <td>
                          <Link to={'/testimonial-detail/' + item._id} style={{ color: 'inherit', textDecoration: 'none' }}>
                            <div style={{ fontWeight: 600, color: 'var(--branch-text)' }}>{item.clientName || '-'}</div>
                            {item.clientTitle && <div style={{ fontSize: '0.75rem', color: 'var(--branch-text-muted)' }}>{item.clientTitle}</div>}
                          </Link>
                        </td>
                        <td>
                          {item.category ? (
                            <span className={'testimonial-category-badge testimonial-category-' + item.category}>
                              {item.category}
                            </span>
                          ) : (
                            <span className='testimonial-category-badge testimonial-category-none'>—</span>
                          )}
                        </td>
                        <td>
                          {item.videoUrl ? (
                            <span className='testimonial-video-badge testimonial-video-yes' title={item.videoUrl}>
                              <FiVideo size={14} />
                            </span>
                          ) : (
                            <span className='testimonial-video-badge testimonial-video-no'>
                              <FiVideo size={14} />
                            </span>
                          )}
                        </td>
                        <td>
                          <div className='testimonial-review-excerpt' title={item.reviewText}>{item.reviewText || '-'}</div>
                        </td>
                        <td>
                          {!item.isDeleted && (
                            <label className='branch-toggle-sm' title={item.isActive ? 'Deactivate' : 'Activate'}>
                              <input
                                type='checkbox'
                                checked={!!item.isActive}
                                onChange={() => handleToggle(item)}
                              />
                              <span className='branch-toggle-slider' />
                            </label>
                          )}
                        </td>
                        <td>
                          <BranchActionIcons
                            branch={item}
                            onDelete={handleDelete}
                            onRestore={handleActivate}
                            editPath='/testimonial-update/'
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!sortMode && (
                <BranchPagination
                  page={page}
                  pageCount={pageCount}
                  totalCount={totalCount}
                  perPage={perPage}
                  onChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
